import { useCategories } from '@core/hooks/useCategories';
import { useInventory } from '@core/hooks/useInventory';
import { Drawer, ConfirmModal } from '@core/ui';
import {
    Plus, X, Tag, Trash2,
    Layers, AlertCircle, Save
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

/**
 * CategoryManager - Technical manager for official categories.
 */
const CategoryManager = ({ isOpen, onClose }) => {
    const { categories, loading, addCategory, deleteCategory, syncFromProducts } = useCategories();
    const { products } = useInventory();
    const [newCategory, setNewCategory] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null, name: '' });

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newCategory.trim()) return;

        setIsSubmitting(true);
        try {
            await addCategory(newCategory);
            setNewCategory('');
            toast.success('Category added successfully');
        } catch (err) {
            toast.error(err.message || 'Error adding category');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSync = async () => {
        const productCategories = products.map(p => p.category);
        setIsSubmitting(true);
        try {
            const count = await syncFromProducts(productCategories);
            toast.success(`${count} categories synced from items.`);
        } catch (err) {
            toast.error('Error syncing');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id, name) => {
        setConfirmModal({ isOpen: true, id, name });
    };

    const confirmDelete = async () => {
        const { id } = confirmModal;
        try {
            await deleteCategory(id);
            toast.success('Category deleted');
        } catch (err) {
            toast.error('Error deleting category');
        } finally {
            setConfirmModal({ isOpen: false, id: null, name: '' });
        }
    };

    return (
        <>
            <Drawer
                isOpen={isOpen}
                onClose={onClose}
                title="Category Management"
                subtitle="Manage the official classification catalog"
                icon={Layers}
                width="max-w-md"
            >
                <div className="flex flex-col gap-8">

                    {/* 1. Quick Add Form */}
                    <form onSubmit={handleAdd} className="flex flex-col gap-3 p-6 bg-gray-50/50 rounded-[2rem] border border-gray-100 shadow-inner">
                        <label htmlFor="new-category" className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 ml-1">New Category</label>
                        <div className="flex gap-2">
                            <input
                                id="new-category"
                                type="text"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                placeholder="e.g., Bearings..."
                                className="flex-1 px-6 py-4 bg-white border-2 border-transparent rounded-2xl outline-none focus:border-accent/30 font-bold text-primary transition-all shadow-sm"
                            />
                            <button
                                type="submit"
                                disabled={isSubmitting || !newCategory.trim()}
                                className="w-14 h-14 bg-accent text-primary-dark rounded-2xl flex items-center justify-center hover:shadow-lg disabled:opacity-50 transition-all active:scale-90"
                            >
                                {isSubmitting ? <span className="animate-spin text-xl">...</span> : <Plus size={24} strokeWidth={3} />}
                            </button>
                        </div>
                    </form>

                    {/* 2. Categories List */}
                    <div className="flex flex-col gap-3">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 ml-1 flex items-center gap-2">
                            <Tag size={12} className="text-primary" /> Available Categories ({categories.length})
                        </label>
                        <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto pr-2 scrollbar-premium">
                            {loading ? (
                                <div className="p-10 text-center text-slate-400 font-bold italic">Loading catalog...</div>
                            ) : categories.length === 0 ? (
                                <div className="p-10 text-center text-slate-300 italic flex flex-col items-center gap-4">
                                    <AlertCircle size={32} />
                                    <span className="text-sm">No categories configured yet.</span>
                                </div>
                            ) : (
                                categories.map((cat) => (
                                    <div
                                        key={cat.id}
                                        className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl group hover:border-slate-200 hover:shadow-md transition-all animate-in fade-in slide-in-from-left-2 duration-300"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-1.5 h-6 bg-slate-200 group-hover:bg-amber-600 rounded-full transition-colors" />
                                            <span className="font-bold text-slate-700">{cat.name}</span>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(cat.id, cat.name)}
                                            className="p-2 text-slate-400 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                                            title="Delete Category"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* 3. Info Footer */}
                    <div className="mt-4 p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-start gap-3">
                        <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-[10px] font-bold text-amber-700 leading-relaxed">
                            These categories will be available globally in the item creation selector.
                            Ensure you maintain standardized technical names.
                        </p>
                    </div>

                    <div className="pt-6 border-t border-slate-100">
                        <button
                            onClick={onClose}
                            className="w-full py-5 bg-slate-900 text-white font-black rounded-2xl uppercase tracking-[0.2em] text-[10px] hover:shadow-2xl transition-all active:scale-95"
                        >
                            Done, Close
                        </button>
                    </div>
                </div>
            </Drawer>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, id: null, name: '' })}
                onConfirm={confirmDelete}
                title="Delete Category?"
                message={`Are you sure you want to delete "${confirmModal.name}"? Items using it will be left without an official label.`}
                confirmText="DELETE CATEGORY"
                type="danger"
            />
        </>
    );
};

export default CategoryManager;
